import './dropDowns.css';

import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import LockIcon from '@mui/icons-material/Lock';
import { translate } from '../../../../common/language/translations';
import { UserConfig, UserConfigMap } from '../../../types/user';
import { getAccessIcon } from '../../../utils/iconUtils';


interface UserDropDownProps {
  id: string;

  userMap: UserConfigMap;
  dropDownOptions: UserConfig[];
  selectedOptions: string[];
  setSelectedOptions: (value: string[]) => void;

  isDropDownExpanded: boolean;
  setIsDropDownExpanded: (value: boolean) => void;

  defaultDropDownText: string;
  defaultDropDownTextColor: string;
  marginBetweenOptions: number;
  height?: number;
  maxHeight?: string;
  multiSelect?: boolean;

  dropDownDisabled?: boolean;
}

export const UserDropDown: React.FC<UserDropDownProps> = ({
  id,

  userMap,
  dropDownOptions,

  selectedOptions,
  setSelectedOptions,
  isDropDownExpanded,
  setIsDropDownExpanded,

  defaultDropDownText,
  defaultDropDownTextColor,
  marginBetweenOptions,
  height,
  maxHeight,
  multiSelect,

  dropDownDisabled,
}) => {

  const userConfig = useRecoilValue(userConfigState);

  const dropDownRef = useRef<HTMLDivElement>(null);


  const handleDropDownClick = () => {
    if (isDropDownExpanded) {
      removeDropDownExpansion();
    }

    else {
      expandDropDown();
    }
  };


  const handleOptionSelect = (email: string, isOptionAlreadySelected: boolean) => {

    let newSelectedOptions: string[] = [];

    // single-select
    if (!multiSelect) {
      if (!isOptionAlreadySelected) {
        removeDropDownExpansion();
      }
      newSelectedOptions = isOptionAlreadySelected ? [] : [email];
    }

    // multi-select
    else {
      newSelectedOptions = selectedOptions.slice();
      if (isOptionAlreadySelected) {
        const i = newSelectedOptions.indexOf(email);
        newSelectedOptions.splice(i, 1);
      }
      else {
        newSelectedOptions.push(email);
      }
    }

    setSelectedOptions(newSelectedOptions);
  };


  const expandDropDown = () => {
    const element = document.getElementById(id);
    if (element) {
      element.style.transition = 'height 150ms';
      element.style.height = 'auto';
      element.style.zIndex = '100';
    }
    setIsDropDownExpanded(true);
  };


  const removeDropDownExpansion = () => {
    const element = document.getElementById(id);
    if (element) {
      element.style.transition = '75ms';
      element.style.height = height ? height + 'px' : '28px';
      element.style.zIndex = '1';
    }
    setIsDropDownExpanded(false);
    if (dropDownRef.current) {
      dropDownRef.current.scrollTop = 0;
    }
  };


  const getDisplayOption = (option: UserConfig) => {
    if (!option) return translate('unknownUser', userConfig?.language);
    if (multiSelect) return option.name;
    return option.name + ' (' + option.email + ')';
  };


  const showSelectedOptions = () => {

    // single-select
    let firstSelection = getDisplayOption(userMap[selectedOptions[0]]);

    if (!multiSelect) {
      return firstSelection;
    }

    // multi-select
    for (let i = 1; i < selectedOptions.length; i++) {
      firstSelection += ', ' + getDisplayOption(userMap[selectedOptions[i]]);
    }
    return firstSelection;
  };


  useEffect(() => {
    if (!isDropDownExpanded) {
      removeDropDownExpansion();
    }
  }, [isDropDownExpanded]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div
      className='drop-down-container'
      id={id}
      style={{ height: height, maxHeight: maxHeight }}
    >

      <div
        className='drop-down-button'
        style={{ height: height, pointerEvents: dropDownDisabled ? 'none' : undefined }}
        onClick={() => handleDropDownClick()}
      >

        {selectedOptions.length > 0 && <div className='drop-down-button-overlay' />}

        <div className='drop-down-button-text'>
          {selectedOptions.length === 0 ? (
            <div style={{ color: defaultDropDownTextColor }}>
              {translate(defaultDropDownText, userConfig?.language)}
            </div>
          ) : (
            <div style={{ color: '#000000' }}>
              {showSelectedOptions()}
            </div>
          )}
        </div>

        {!isDropDownExpanded && !dropDownDisabled && (
          <div className='drop-down-icon' style={{ marginTop: height ? (height - 24) / 2 : undefined }}>
            <ArrowDropDownIcon style={{ fontSize: 24 }} />
          </div>
        )}

        {isDropDownExpanded && !dropDownDisabled && (
          <div className='drop-down-icon' style={{ marginTop: height ? (height - 24) / 2 : undefined }}>
            <ArrowDropUpIcon style={{ fontSize: 24 }} />
          </div>
        )}

        {dropDownDisabled && (
          <div className='drop-down-icon' style={{ right: 4, marginTop: height ? (height - 18) / 2 : 5 }}>
            <LockIcon style={{ fontSize: 18, color: '#000000aa' }} />
          </div>
        )}
      </div>

      <div className='drop-down-space'>&nbsp;</div>

      <div
        ref={dropDownRef}
        className={'drop-down-options' + (maxHeight ? ' drop-down-options-scroll' : '')}
        style={{ maxHeight: maxHeight }}
      >

        {dropDownOptions.map(user => {

          const isSelected = selectedOptions.includes(user.email);

          return (
            <div
              className={
                'drop-down-option'
                + (isSelected ? ' drop-down-option-selected' : '')
              }
              style={{ marginTop: marginBetweenOptions }}
              key={user.email}
              onClick={() => handleOptionSelect(user.email, isSelected)}
            >
              <div className='flex-row align-center justify-center' style={{ gap: 2 }}>
                {getAccessIcon(user.accessConfig.accessType, 14, '#000000bb')}
                {user.name}
              </div>
              <div style={{ fontSize: 12, color: '#000000bb', marginTop: 2 }}>
                {user.email}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};
